/**
 * The UK public holiday regions, and what to call them. ONE vocabulary.
 *
 * ⚠️ THERE WERE FOUR COPIES OF THIS AND THEY HAD ALREADY DRIFTED. Found 13/08/2026:
 *
 *     lib/counties.js                    'England & Wales'
 *     lib/companyHolidays.js             'England & Wales'
 *     pages/HolidaysPage.js              'England and Wales'   <- different
 *     components/PublicHolidaysManager.jsx  'England and Wales'   <- different
 *
 * So the same region was called two different things depending on which screen you were
 * looking at. Nobody had reported it, which is the point: this is the kind of fault that
 * never gets reported and quietly makes an app feel like several apps.
 *
 * ⚠️ THE AMPERSAND WINS because that is what the Locations and Settings screens have
 * always shown, and those are the screens where an admin CHOOSES a region.
 *
 * ⚠️ THE REGION CODES ARE DATA, NOT DECORATION. They are stored in
 * `public_holidays.region`, `locations.public_holiday_region` and
 * `tenants.public_holiday_region`. Never change a KEY here to make a label read better.
 */

export const REGION_LABELS = {
  'england-and-wales': 'England & Wales',
  scotland: 'Scotland',
  'northern-ireland': 'Northern Ireland',
};

/**
 * What to call a holiday row's region ON A LIST OF DAYS.
 *
 * ⚠️ AN EMPTY REGION MEANS "EVERYONE IN THIS COUNTRY", NOT "MISSING". It is how a
 * company's own day is stored, and how a country with no sub-divisions (Algeria, Ireland)
 * is stored. Rendering it as blank or "Not set" would tell an admin something is wrong
 * with a row that is perfectly correct.
 */
export const regionLabel = (region) => {
  const code = (region || '').trim();
  if (!code) return 'All regions';
  return REGION_LABELS[code] || code;
};

/**
 * What to call the scope of a HEADLINE bank-holiday figure — "Bank holidays: X".
 *
 * ⚠️ DIFFERENT QUESTION, DIFFERENT FALLBACK, AND THAT IS WHY IT IS A SEPARATE FUNCTION.
 * Here an empty region is not "all regions" — it means the country has no regions to
 * choose between, so the honest label is the COUNTRY.
 *
 * ⚠️ ROB'S ALGERIAN SIGNUP, 13/08/2026, SAID "Bank holidays: Not set — 14 loaded".
 * Nothing was unset: Algeria has no regions. The onboarding page had its own little
 * label function that fell back to the words "Not set", and a customer reading that on
 * their first day would reasonably think the app had lost something.
 */
export const holidayScopeLabel = (countryName, region) =>
  REGION_LABELS[(region || '').trim()] || (countryName || '').trim() || 'your head office';
